import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Trash2, X, PenSquare, Reply, Send, Inbox as InboxIcon, SendHorizontal, Settings2 } from "lucide-react";
import { EmailPanel } from "./EmailPanel";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none transition-colors";

const TABS = [
  { id: "inbox", label: "受信ボックス", icon: InboxIcon },
  { id: "sent", label: "送信済み", icon: SendHorizontal },
  { id: "settings", label: "メール設定", icon: Settings2 },
];

export function MailPanel() {
  const [tab, setTab] = useState("inbox");
  const [boxes, setBoxes] = useState([]);
  const [box, setBox] = useState("info");
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [opening, setOpening] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const folder = tab === "sent" ? "Sent" : "INBOX";

  useEffect(() => {
    api.get("/mail/boxes").then((r) => {
      setBoxes(r.data);
      if (r.data.length && !r.data.find((b) => b.id === box)) setBox(r.data[0].id);
    }).catch(() => setBoxes([]));
    // eslint-disable-next-line
  }, []);

  const load = useCallback(async () => {
    if (tab === "settings") return;
    setError("");
    try {
      const r = await api.get(`/mail/messages?box=${box}&folder=${folder}`);
      setMessages(r.data);
    } catch (err) {
      setMessages([]);
      setError(formatApiError(err.response?.data?.detail) || "メールの取得に失敗しました");
    }
  }, [box, folder, tab]);

  useEffect(() => { load(); }, [load]);

  const open = async (uid) => {
    setOpening(true);
    try {
      const r = await api.get(`/mail/messages/${uid}?box=${box}&folder=${folder}`);
      setSelected(r.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setOpening(false);
    }
  };

  const remove = async (uid) => {
    try {
      await api.delete(`/mail/messages/${uid}?box=${box}&folder=${folder}`);
      toast.success("削除しました");
      setSelected(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const boxLabel = boxes.find((b) => b.id === box)?.address || "";

  return (
    <div className="max-w-4xl mx-auto space-y-4" data-testid="mail-panel">
      {/* タブ */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="mail-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setTab(id); setSelected(null); setMessages(id === "settings" ? messages : null); }}
            data-testid={`mail-tab-${id}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs transition-colors ${
              tab === id ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "settings" ? (
        <EmailPanel embedded />
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* ボックス切替 */}
            <div className="flex items-center gap-1.5" data-testid="mail-boxes">
              {boxes.map((b) => (
                <button
                  key={b.id}
                  onClick={() => { setBox(b.id); setSelected(null); setMessages(null); }}
                  data-testid={`mail-box-${b.id}`}
                  className={`rounded-lg border px-3.5 py-1.5 text-xs font-mono transition-colors ${
                    box === b.id ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-white/50 hover:text-white hover:border-white/25"
                  }`}
                >
                  {b.address}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} data-testid="mail-refresh" className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-gold hover:border-gold/50 transition-colors">
                <RefreshCw size={14} /> 更新
              </button>
              <button onClick={() => setComposeOpen(true)} data-testid="mail-compose-open" className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors">
                <PenSquare size={15} /> 新規メール
              </button>
            </div>
          </div>
          <p className="text-xs text-white/40">
            {tab === "sent" ? `${boxLabel} から送信したメールの履歴です。` : `${boxLabel} の受信メールです。クリックで内容を開き、返信・削除ができます。`}
          </p>

          {error && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="mail-error">{error}</p>
          )}

          {messages === null ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>
          ) : messages.length === 0 && !error ? (
            <p className="py-16 text-center text-white/40 text-sm rounded-xl border border-dashed border-white/10" data-testid="mail-empty">
              {tab === "sent" ? "送信済みメールはありません。" : "受信メールはありません。"}
            </p>
          ) : (
            <div className="space-y-2" data-testid="mail-list">
              {messages.map((m) => (
                <div
                  key={m.uid}
                  onClick={() => open(m.uid)}
                  data-testid={`mail-row-${m.uid}`}
                  className="group flex items-center gap-4 rounded-xl border border-white/10 bg-black/20 p-3.5 cursor-pointer hover:border-gold/40 hover:bg-white/[0.04] transition-all"
                >
                  {!m.seen && tab === "inbox" && <span className="h-2 w-2 rounded-full bg-gold shrink-0" title="未読" />}
                  <div className="min-w-0 flex-1">
                    <p className={`truncate transition-colors ${m.seen || tab === "sent" ? "text-white/70" : "font-jp font-bold text-white"} group-hover:text-gold`}>{m.subject}</p>
                    <p className="text-xs text-white/40 mt-0.5 truncate">{tab === "sent" ? `宛先: ${m.to}` : m.from}</p>
                  </div>
                  <span className="shrink-0 text-xs text-white/40">{m.date.slice(0, 22)}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(m.uid); }}
                    data-testid={`mail-delete-${m.uid}`}
                    className="shrink-0 p-1.5 text-white/40 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {opening && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-black/60"><Loader2 className="animate-spin text-gold" /></div>
      )}
      {selected && (
        <MailDetailModal
          msg={selected}
          box={box}
          folder={folder}
          canReply={tab === "inbox"}
          onClose={() => setSelected(null)}
          onDelete={() => remove(selected.uid)}
          onReplied={() => setSelected(null)}
        />
      )}
      {composeOpen && (
        <ComposeModal box={box} boxLabel={boxLabel} onClose={() => setComposeOpen(false)} onSent={() => setComposeOpen(false)} />
      )}
    </div>
  );
}

function MailDetailModal({ msg, box, folder, canReply, onClose, onDelete, onReplied }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const sendReply = async () => {
    if (!reply.trim()) { toast.error("返信内容を入力してください"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/mail/messages/${msg.uid}/reply`, { body: reply, box, folder });
      toast.success(`${r.data.to} へ返信しました`);
      onReplied();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="mail-detail-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">{canReply ? "Received Mail" : "Sent Mail"}</p>
            <p className="font-jp font-bold text-lg text-white truncate mt-0.5">{msg.subject}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="mail-detail-close" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>
        <div className="px-6 sm:px-8 py-6 space-y-4 overflow-y-auto">
          <dl className="space-y-2 text-sm">
            <div className="flex gap-4"><dt className="w-16 text-white/40 shrink-0">差出人</dt><dd className="text-white/90 break-all">{msg.from}</dd></div>
            <div className="flex gap-4"><dt className="w-16 text-white/40 shrink-0">宛先</dt><dd className="text-white/90 break-all">{msg.to}</dd></div>
            <div className="flex gap-4"><dt className="w-16 text-white/40 shrink-0">日時</dt><dd className="text-white/90">{msg.date}</dd></div>
          </dl>
          <div className="rounded-lg bg-white/[0.03] border border-white/10 p-4 text-sm text-white/85 leading-relaxed whitespace-pre-wrap" data-testid="mail-detail-body">{msg.body}</div>
          {canReply && (
            <div>
              <label className="flex items-center gap-1.5 text-xs text-white/50 mb-1.5"><Reply size={12} /> 返信</label>
              <textarea data-testid="mail-reply-body" rows={4} className={`${inputCls} resize-y`} placeholder="返信内容を入力…" value={reply} onChange={(e) => setReply(e.target.value)} />
            </div>
          )}
        </div>
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          {canReply && (
            <button type="button" onClick={sendReply} disabled={busy} data-testid="mail-reply-send" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} 返信する
            </button>
          )}
          <button type="button" onClick={onDelete} data-testid="mail-detail-delete" className="inline-flex items-center gap-2 rounded-lg border border-red-400/40 px-4 py-2.5 text-sm text-red-300 hover:bg-red-400/10 transition-colors">
            <Trash2 size={14} /> 削除
          </button>
          <button type="button" onClick={onClose} className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">閉じる</button>
        </div>
      </div>
    </div>
  );
}

function ComposeModal({ box, boxLabel, onClose, onSent }) {
  const [form, setForm] = useState({ to: "", subject: "", body: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const send = async () => {
    if (!form.to || !form.subject || !form.body) { toast.error("宛先・件名・本文を入力してください"); return; }
    setBusy(true);
    try {
      await api.post("/mail/send", { ...form, box });
      toast.success(`${form.to} へ送信しました`);
      onSent();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="mail-compose-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">New Mail</p>
            <p className="font-jp font-bold text-lg text-white mt-0.5">新規メール作成</p>
          </div>
          <button type="button" onClick={onClose} data-testid="mc-cancel-x" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>
        <div className="px-6 sm:px-8 py-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs text-white/50 mb-1.5">宛先メールアドレス *</label>
            <input data-testid="mc-to" type="email" className={inputCls} placeholder="example@example.com" value={form.to} onChange={set("to")} />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">件名 *</label>
            <input data-testid="mc-subject" className={inputCls} value={form.subject} onChange={set("subject")} />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1.5">本文 *</label>
            <textarea data-testid="mc-body" rows={10} className={`${inputCls} resize-y`} value={form.body} onChange={set("body")} />
          </div>
          <p className="text-[11px] text-white/35">送信元: {boxLabel}</p>
        </div>
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          <button type="button" onClick={send} disabled={busy} data-testid="mc-send" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} 送信する
          </button>
          <button type="button" onClick={onClose} data-testid="mc-cancel" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">キャンセル</button>
        </div>
      </div>
    </div>
  );
}
